import React, { useCallback, useEffect, useState } from "react";
import { Form, Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast, Bounce } from "react-toastify";
import { useTranslate } from "../hooks/LanguageProvider";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

interface SupplierInterface {
  id_supplier?: number;
  legal_name: string;
  trade_name: string;
  rc_number: string;
  nif: string;
  nis: string;
  address?: string;
  wilaya: string;
  geo_lat?: number;
  geo_lng?: number;
  website: string;
  workforce_total: number;
  industrial_status: string;
  maturity_score: number;
}

export default function Supplier() {
  const { id_supplier } = useParams();
  const navigate = useNavigate();
  const { translate } = useTranslate();

  const [supplier, setSupplier] = useState<SupplierInterface>({
    legal_name: "",
    trade_name: "",
    rc_number: "",
    nif: "",
    nis: "",
    address: "",
    wilaya: "",
    geo_lat: 0,
    geo_lng: 0,
    website: "",
    workforce_total: 0,
    industrial_status: "",
    maturity_score: 0,
  });

  const [buttonClicked, setButtonClicked] = useState(false);

  const getSupplier = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/find/${id_supplier}`);
      const data = await res.json();
      setSupplier(data);
    } catch (error) {
      console.error(error);
    }
  }, [id_supplier]);

  useEffect(() => {
    if (id_supplier) {
      getSupplier();
    }
  }, [getSupplier, id_supplier]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setSupplier((prev) => ({
      ...prev,
      [name]: name === "workforce_total" ? Number(value) : value,
    }));
  };

  const createSupplier = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${backendUrl}/api/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...supplier,
          id_user: user.id_user,
          address: "",
          geo_lat: 0,
          geo_lng: 0,
          industrial_status: "active",
          maturity_score: 0,
        }),
      });

      if (!res.ok) {
        toast.warn(translate("cannot_create_supplier"), { transition: Bounce });
        setButtonClicked(false);
        return;
      }

      toast.success(translate("supplier_created_successfully"), { transition: Bounce });
      navigate("/Suppliers");
    } catch (error) {
      console.error(error);
      setButtonClicked(false);
    }
  };

  const updateSupplier = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(supplier),
      });

      if (!res.ok) {
        toast.warn(translate("cannot_update_supplier"), { transition: Bounce });
        setButtonClicked(false);
        return;
      }

      toast.success(translate("supplier_updated_successfully"), { transition: Bounce });
      navigate("/Suppliers");
    } catch (error) {
      console.error(error);
      setButtonClicked(false);
    }
  };

  return (
    <div className="container mt-4">
      <h4 className="mb-4">
        {id_supplier ? translate("edit_supplier") : translate("new_supplier")}
      </h4>

      <Form>
        <Form.Group className="mb-3">
          <Form.Label>{translate("legal_name")}</Form.Label>
          <Form.Control
            name="legal_name"
            value={supplier.legal_name}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{translate("trade_name")}</Form.Label>
          <Form.Control
            name="trade_name"
            value={supplier.trade_name}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{translate("rc_number")}</Form.Label>
          <Form.Control
            name="rc_number"
            value={supplier.rc_number}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{translate("nif")}</Form.Label>
          <Form.Control
            name="nif"
            value={supplier.nif}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{translate("nis")}</Form.Label>
          <Form.Control
            name="nis"
            value={supplier.nis}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{translate("wilaya")}</Form.Label>
          <Form.Control
            name="wilaya"
            value={supplier.wilaya}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{translate("website")}</Form.Label>
          <Form.Control
            name="website"
            value={supplier.website}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{translate("workforce")}</Form.Label>
          <Form.Control
            type="number"
            name="workforce_total"
            value={supplier.workforce_total}
            onChange={handleChange}
          />
        </Form.Group>

        <Button
          type="button"
          variant="primary"
          disabled={buttonClicked}
          onClick={() => {
            setButtonClicked(true);
            id_supplier ? updateSupplier() : createSupplier();
          }}
        >
          {id_supplier ? translate("save_changes") : translate("create_supplier")}
        </Button>

        <Button
          type="button"
          variant="secondary"
          className="ms-2"
          onClick={() => navigate("/Suppliers")}
        >
          {translate("cancel")}
        </Button>
      </Form>
    </div>
  );
}
